import {
  DbBusiness,
  DbBusinessState,
  DbBusinessReport,
  DbBusinessModule,
  DbBusinessImage,
  DbBusinessConfig,
  DbBusinessAccountSummary,
  DbBusinessBankDetail,
  DbBusinessBillingPurchase,
  DbBusinessVisibility,
} from "./models";
import { firebaseDb } from "../firebase/client-app";
import {
  arrayRemove,
  arrayUnion,
  collection,
  collectionGroup,
  doc,
  Firestore,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  startAfter,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore/lite";
import { axiosInstance } from "../rest/axios";
import { getCookie } from "cookies-next";
import { R2Module } from "../r2/model";
import { r2Service } from "../r2/service";
import { memberService } from "../member/service";
import { DbBusinessMemberRole } from "../member/models";
import { serviceCategoryService } from "../category-service/service";
import { DbBusinessWhatsAppHeaderType } from "../whatsapp/models";

export class BusinessService {
  private businessesRef(firestore: Firestore) {
    return collection(firestore, "businesses");
  }

  private businessConfigRef(firestore: Firestore) {
    return collection(firestore, "business_config");
  }

  private reportsRef(firestore: Firestore, businessId: string) {
    return collection(
      firestore,
      "businesses/" + businessId + "/business_reports",
    );
  }

  private businessPrivateRef(firestore: Firestore, businessId: string) {
    return collection(
      firestore,
      "businesses/" + businessId + "/business_private",
    );
  }

  private businessBillingPurchaseRef(firestore: Firestore, businessId: string) {
    return collection(
      firestore,
      `businesses/${businessId}/business_private/billing/purchases`,
    );
  }

  generateReportId(firestore: Firestore, businessId: string): string {
    return doc(this.reportsRef(firestore, businessId)).id;
  }

  generateBusinessId(firestore: Firestore): string {
    return doc(this.businessesRef(firestore)).id;
  }

  async getBusinesses(
    statuses: DbBusinessState[] = [DbBusinessState.Approved],
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusiness[]> {
    const validStatuses = Object.values(DbBusinessState).filter((status) =>
      statuses.includes(status),
    );
    if (validStatuses.length === 0) {
      throw new Error("Invalid statuses");
    }

    const businesses = await getDocs(
      query(
        this.businessesRef(firestore),
        where("business_state", "in", validStatuses),
        where("is_deleted", "==", false),
      ),
    );
    return businesses.docs.map((doc) => doc.data() as DbBusiness);
  }

  async getAllBusinesses(
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusiness[]> {
    const businesses = await getDocs(query(this.businessesRef(firestore)));
    return businesses.docs.map((doc) => doc.data() as DbBusiness);
  }

  async getBusinessesByVisibility(
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusiness[]> {
    const businesses = await getDocs(
      query(
        this.businessesRef(firestore),
        where("business_state", "==", DbBusinessState.Approved),
        where("is_deleted", "==", false),
        where("visibility", "in", [
          DbBusinessVisibility.Public,
          DbBusinessVisibility.Protected,
        ]),
      ),
    );
    return businesses.docs.map((doc) => doc.data() as DbBusiness);
  }

  async getBusinessByUrl(
    url: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusiness | null> {
    const business = await getDocs(
      query(this.businessesRef(firestore), where("business_url", "==", url)),
    );

    if (business.docs.length === 0) {
      return null;
    }

    const businessData = business.docs[0].data() as DbBusiness;

    // Don't return deleted businesses
    if (businessData.is_deleted) {
      return null;
    }

    return businessData;
  }

  async getBusiness(
    businessId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusiness | null> {
    const business = await getDoc(
      doc(this.businessesRef(firestore), businessId),
    );
    if (!business.exists()) {
      return null;
    }
    return business.data() as DbBusiness;
  }

  async getBusinessesById(
    ids: string[],
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusiness[]> {
    const businesses = await Promise.all(
      ids.map((id) => this.getBusiness(id, firestore)),
    );
    return businesses.filter((business) => business !== null) as DbBusiness[];
  }

  async isBusinessReportedByUser(
    userId: string,
    businessId: string,
    firestore: Firestore = firebaseDb,
  ) {
    const reportSnapshot = await getDocs(
      query(
        this.reportsRef(firestore, businessId),
        where("user_id", "==", userId),
        limit(1),
      ),
    );

    return !reportSnapshot.empty;
  }

  async createReport(
    report: DbBusinessReport,
    businessId: string,
    firestore: Firestore = firebaseDb,
  ) {
    const reportId = this.generateReportId(firestore, businessId);

    await setDoc(doc(this.reportsRef(firestore, businessId), reportId), {
      ...report,
      id: reportId,
      created_at: Timestamp.now(),
    });
  }

  async getBusinessesByUserId(
    id: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusiness[]> {
    const membersSnapshot = await getDocs(
      query(
        collectionGroup(firestore, "business_members"),
        where("id", "==", id),
      ),
    );

    const businessIds = new Set<string>();
    membersSnapshot.forEach((doc) => {
      businessIds.add(doc.ref.parent.parent!.id);
    });

    return this.getBusinessesById(Array.from(businessIds), firestore);
  }

  async saveBusiness(
    data: DbBusiness,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusiness> {
    data.id = this.generateBusinessId(firestore);

    try {
      await setDoc(doc(this.businessesRef(firestore), data.id), data);

      // save business member as owner
      await memberService.addBusinessMember(
        data.id,
        data.created_by,
        DbBusinessMemberRole.Owner,
      );

      const business = await this.getBusiness(data.id, firestore);
      if (!business) {
        throw new Error("Failed to save business");
      }
      return Promise.resolve(business);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async updateModules(
    businessId: string,
    modules: DbBusinessModule[],
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    await updateDoc(doc(this.businessesRef(firestore), businessId), {
      modules: modules,
    });
  }

  async updateVisibility(
    businessId: string,
    visibility: string,
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    await updateDoc(doc(this.businessesRef(firestore), businessId), {
      visibility: visibility,
    });
  }

  async updateBusinessDetails(
    businessId: string,
    businessDetails: Partial<
      Pick<
        DbBusiness,
        "business_url" | "description2" | "name" | "phone" | "appointment_phone"
      >
    >,
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    await updateDoc(
      doc(this.businessesRef(firestore), businessId),
      businessDetails,
    );
  }

  async addBusinessImage(
    businessId: string,
    file: File,
    isPrimary: boolean = false,
    blurHash?: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessImage> {
    const imageUrl = await r2Service.upload(
      file,
      R2Module.business,
      businessId,
    );

    const newImage: DbBusinessImage = {
      url: imageUrl,
      is_primary: isPrimary,
      blur_hash: blurHash,
    };

    await updateDoc(doc(this.businessesRef(firestore), businessId), {
      images: arrayUnion(newImage),
    });
    return newImage;
  }

  async removeBusinessImage(
    businessId: string,
    image: DbBusinessImage,
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    await r2Service.deleteFile(R2Module.business, image.url, businessId);
    await updateDoc(doc(this.businessesRef(firestore), businessId), {
      images: arrayRemove(image),
    });
  }

  async updateBusinessImages(
    businessId: string,
    images: DbBusinessImage[],
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    await updateDoc(doc(this.businessesRef(firestore), businessId), {
      images: images,
    });
  }

  async updateMaxBookingWindow(
    businessId: string,
    max_booking_window: number,
    firestore: Firestore = firebaseDb,
  ) {
    if (!businessId.trim()) {
      throw new Error("Business ID is required");
    }

    await updateDoc(doc(this.businessesRef(firestore), businessId), {
      max_booking_window: max_booking_window,
    });
  }

  async updateCancellationWindow(
    businessId: string,
    cancellation_window: number | null,
    firestore: Firestore = firebaseDb,
  ) {
    if (!businessId.trim()) {
      throw new Error("Business ID is required");
    }

    await updateDoc(doc(this.businessesRef(firestore), businessId), {
      cancellation_window: cancellation_window,
    });
  }

  async deleteBusiness(
    businessId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    await updateDoc(doc(this.businessesRef(firestore), businessId), {
      is_deleted: true,
    });
  }
  async updateBusinessHours(
    businessId: string,
    business_hours: any,
    firestore: Firestore = firebaseDb,
  ) {
    if (!businessId.trim()) {
      throw new Error("Business ID is required");
    }

    await updateDoc(doc(this.businessesRef(firestore), businessId), {
      business_hours: business_hours,
    });
  }
  async getBusinessConfig(
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessConfig[]> {
    const configQuery = query(
      this.businessConfigRef(firestore),
      orderBy("price"),
    );
    const businessConfig = await getDocs(configQuery);
    return businessConfig.docs.map((doc) => doc.data() as DbBusinessConfig);
  }
  async sendWhatsAppAlert(businessId: string, paymentId: string): Promise<any> {
    const res = await axiosInstance.post(
      "/business/purchase/alerts-whatsapp",
      {
        business_id: businessId,
        payment_id: paymentId,
      },
      { headers: { Authorization: getCookie("Authorization")?.toString() } },
    );

    return res.data;
  }

  isEmptyBusiness(obj: DbBusiness) {
    return obj && typeof obj === "object" && Object.keys(obj).length === 0;
  }
  async getBusinessAccountSummary(
    businessId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessAccountSummary> {
    const summary = await getDoc(
      doc(this.businessPrivateRef(firestore, businessId), "account_summary"),
    );

    return summary.data() as DbBusinessAccountSummary;
  }
  async getBusinessBankDetail(
    businessId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessBankDetail> {
    const details = await getDoc(
      doc(this.businessPrivateRef(firestore, businessId), "bank_details"),
    );

    return details.data() as DbBusinessBankDetail;
  }
  async updateWhatsAppAlertsSetting(
    businessId: string,
    allowedAlerts: string[],
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    const alertsDocRef = doc(
      this.businessPrivateRef(firestore, businessId),
      "alerts_whatsapp",
    );
    await updateDoc(alertsDocRef, {
      allowed_alerts: allowedAlerts,
    });
  }

  async getBusinessBillingHistory(
    businessId: string,
    startAfterDate?: Timestamp,
    pageSize: number = 10,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessBillingPurchase[]> {
    let q = query(
      this.businessBillingPurchaseRef(firestore, businessId),
      orderBy("created_at", "desc"),
      limit(pageSize),
    );

    if (startAfterDate) {
      q = query(q, startAfter(startAfterDate));
    }

    const purchaseDocs = await getDocs(q);
    return purchaseDocs.docs.map(
      (doc) => doc.data() as DbBusinessBillingPurchase,
    );
  }
  async updateHasServiceState(
    businessId: string,
    firestore = firebaseDb,
  ): Promise<void> {
    const response =
      await serviceCategoryService.getCategoriesAndServices(businessId);
    const serviceCount = response.services.length;
    const richServiceCount = response.categories.reduce(
      (acc, category) => acc + category.services.length,
      0,
    );
    const hasService = serviceCount > 0 || richServiceCount > 0;

    await setDoc(
      doc(this.businessesRef(firestore), businessId),
      {
        has_service: hasService,
      },
      { merge: true },
    );
  }
  async updateWhatsAppHeaderType(
    businessId: string,
    headerType: DbBusinessWhatsAppHeaderType,
    firestore: Firestore = firebaseDb,
  ) {
    if (!businessId?.trim()) {
      throw new Error("Business ID is required");
    }

    await updateDoc(doc(this.businessesRef(firestore), businessId), {
      whatsapp_header_type: headerType,
    });
  }
}

export const businessService = new BusinessService();
